import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Chip,
  Badge,
  Avatar,
  IconButton,
  Grid,
  FormControlLabel,
  Checkbox,
  Stack,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Fab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Skeleton,
  Alert,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import {
  Groups as GroupsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";

import apiFetch from "services/http";
import { useSeason } from "context/SeasonContext";
import { PageToolbar, ModalShell, ConfirmDialog, EmptyState, useNotify } from "components/Convokko";

const entrenadorVacio = {
  _id: null,
  nombre: "",
  rol: "entrenador",
  fecha_nacimiento: "",
  activo: true,
  email: "",
  dni: "",
  equipos_actuales: [],
  password: "",
};

const getInitials = (nombre = "") =>
  `${nombre?.[0] || ""}${nombre?.split(" ")[1]?.[0] || ""}`.toUpperCase();

export default function EntrenadoresDashboard() {
  const notify = useNotify();
  const { temporada } = useSeason();

  const [entrenadores, setEntrenadores] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [equipoFiltro, setEquipoFiltro] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("");
  const [rolFiltro, setRolFiltro] = useState("");
  // Vista lista por defecto (más densa); se recuerda la preferencia del usuario
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("convokko_vista_entrenadores") || "list"
  );

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem("convokko_vista_entrenadores", mode);
  };

  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [currentEntrenador, setCurrentEntrenador] = useState(entrenadorVacio);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [dataEntrenadores, dataEquipos] = await Promise.all([
        apiFetch("/api/entrenadores"),
        apiFetch("/api/equipos"),
      ]);
      setEntrenadores(Array.isArray(dataEntrenadores) ? dataEntrenadores : []);
      setEquipos(Array.isArray(dataEquipos) ? dataEquipos : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const equiposById = useMemo(() => {
    const map = {};
    equipos.forEach((e) => {
      map[e._id || e.id] = e;
    });
    return map;
  }, [equipos]);

  const equiposTemporada = useMemo(
    () => equipos.filter((e) => Number(e.temporada) === Number(temporada)),
    [equipos, temporada]
  );

  const getEquipoName = (entrenador) => entrenador.nombre_equipo_actual || "Sin equipo";

  const filteredEntrenadores = useMemo(
    () =>
      entrenadores
        .filter((entrenador) => {
          const misEquipos = entrenador.equipos_actuales || [];
          const enTemporada = misEquipos.some(
            (id) => Number(equiposById[id]?.temporada) === Number(temporada)
          );
          if (misEquipos.length > 0 && !enTemporada) return false;
          if (equipoFiltro && !misEquipos.includes(equipoFiltro)) return false;
          if (rolFiltro && entrenador.rol !== rolFiltro) return false;
          if (statusFiltro === "Activo" && !entrenador.activo) return false;
          if (statusFiltro === "Inactivo" && entrenador.activo) return false;
          if (searchTerm) {
            const q = searchTerm.toLowerCase();
            const target = `${entrenador.nombre || ""} ${entrenador.email || ""} ${
              entrenador.dni || ""
            } ${getEquipoName(entrenador)}`.toLowerCase();
            if (!target.includes(q)) return false;
          }
          return true;
        })
        .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es")),
    [entrenadores, equiposById, temporada, equipoFiltro, rolFiltro, statusFiltro, searchTerm]
  );

  const handleOpenCreate = () => {
    setEditMode(false);
    setCurrentEntrenador(entrenadorVacio);
    setShowPassword(false);
    setOpenModal(true);
  };

  const handleOpenEdit = (entrenador) => {
    setEditMode(true);
    setCurrentEntrenador({
      _id: entrenador._id,
      nombre: entrenador.nombre || "",
      rol: entrenador.rol || "entrenador",
      fecha_nacimiento: entrenador.fecha_nacimiento || "",
      activo: Boolean(entrenador.activo),
      email: entrenador.email || "",
      dni: entrenador.dni || "",
      equipos_actuales: entrenador.equipos_actuales || [],
      password: "",
    });
    setShowPassword(false);
    setOpenModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentEntrenador((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        nombre: currentEntrenador.nombre,
        rol: currentEntrenador.rol || "entrenador",
        fecha_nacimiento: currentEntrenador.fecha_nacimiento,
        dni: currentEntrenador.dni,
        activo: currentEntrenador.activo,
        email: currentEntrenador.email,
        equipos_actuales: currentEntrenador.equipos_actuales || [],
      };
      if (editMode) {
        await apiFetch("/api/entrenadores/actualizar-entrenador", {
          method: "PUT",
          body: { ...payload, _id: currentEntrenador._id },
        });
        notify.success("Entrenador actualizado");
      } else {
        if (!currentEntrenador.password) {
          notify.error("La contraseña es obligatoria para crear un nuevo entrenador");
          setSaving(false);
          return;
        }
        await apiFetch("/api/entrenadores/crear-entrenador", {
          method: "POST",
          body: { ...payload, password: currentEntrenador.password },
        });
        notify.success("Entrenador creado");
      }
      setOpenModal(false);
      await fetchData();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      await apiFetch("/api/entrenadores/eliminar-entrenador", {
        method: "DELETE",
        params: { entrenador_id: deleteTarget._id },
      });
      setEntrenadores((prev) => prev.filter((e) => e._id !== deleteTarget._id));
      notify.success("Entrenador eliminado");
      setDeleteTarget(null);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const renderSkeleton = () => (
    <Grid container spacing={3}>
      {[...Array(8)].map((_, i) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
          <Card sx={{ p: 3, borderRadius: 3 }}>
            <Stack alignItems="center" spacing={2}>
              <Skeleton variant="circular" width={56} height={56} />
              <Skeleton variant="text" width="75%" height={24} />
              <Skeleton variant="rectangular" width="100%" height={28} sx={{ borderRadius: 2 }} />
            </Stack>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <PageToolbar
          icon={GroupsIcon}
          title="Entrenadores"
          count={filteredEntrenadores.length}
          countLabel={filteredEntrenadores.length === 1 ? "entrenador" : "entrenadores"}
          action={{
            label: "Nuevo entrenador",
            shortLabel: "Nuevo",
            icon: <AddIcon />,
            onClick: handleOpenCreate,
          }}
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            placeholder: "Buscar por nombre, email, DNI o equipo...",
          }}
          filters={[
            {
              id: "equipo",
              label: "Equipo",
              allLabel: "Todos los equipos",
              value: equipoFiltro,
              onChange: setEquipoFiltro,
              options: equiposTemporada.map((e) => ({ value: e._id || e.id, label: e.nombre })),
              minWidth: 130,
            },
            {
              id: "rol",
              label: "Rol",
              allLabel: "Todos los roles",
              value: rolFiltro,
              onChange: setRolFiltro,
              options: [
                { value: "entrenador", label: "Entrenador" },
                { value: "coordinador", label: "Coordinador" },
              ],
              minWidth: 100,
            },
            {
              id: "estado",
              label: "Estado",
              allLabel: "Todos los estados",
              value: statusFiltro,
              onChange: setStatusFiltro,
              options: [
                { value: "Activo", label: "Activos" },
                { value: "Inactivo", label: "Inactivos" },
              ],
              minWidth: 100,
            },
          ]}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {loading ? (
          renderSkeleton()
        ) : filteredEntrenadores.length === 0 ? (
          <EmptyState
            icon={GroupsIcon}
            title="No hay entrenadores"
            subtitle={
              entrenadores.length === 0
                ? "Aún no tienes entrenadores registrados."
                : "Ningún entrenador coincide con los filtros o la temporada seleccionada."
            }
            actionLabel="Añadir entrenador"
            onAction={handleOpenCreate}
          />
        ) : viewMode === "list" ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "grey.200",
              overflow: "hidden",
            }}
          >
            <List disablePadding>
              {filteredEntrenadores.map((entrenador, index) => (
                <ListItem
                  key={entrenador._id}
                  divider={index < filteredEntrenadores.length - 1}
                  sx={{ py: 1, px: { xs: 2, sm: 3 }, "&:hover": { bgcolor: "grey.100" } }}
                >
                  <ListItemAvatar>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      badgeContent={
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: entrenador.activo ? "success.main" : "grey.400",
                            border: "2px solid #fff",
                          }}
                        />
                      }
                    >
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          fontWeight: 700,
                          bgcolor: "rgba(34,51,107,0.10)",
                          color: "#22336B",
                        }}
                      >
                        {getInitials(entrenador.nombre)}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    disableTypography
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2" fontWeight={700}>
                          {entrenador.nombre}
                        </Typography>
                        {entrenador.rol === "coordinador" && (
                          <Chip
                            label="Coordinador"
                            color="secondary"
                            size="small"
                            sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700 }}
                          />
                        )}
                      </Stack>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        noWrap
                        display="block"
                        mt={0.25}
                      >
                        {[getEquipoName(entrenador), entrenador.email].filter(Boolean).join(" · ")}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(entrenador)}
                        sx={{ color: "text.secondary", "&:hover": { color: "info.main" } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTarget(entrenador)}
                        sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filteredEntrenadores.map((entrenador) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={entrenador._id}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "grey.200",
                    height: "100%",
                    transition: "all .2s ease",
                    opacity: entrenador.activo ? 1 : 0.65,
                    "&:hover": {
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <Box p={2.5}>
                    <Stack alignItems="center" spacing={1.5}>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        badgeContent={
                          <Box
                            sx={{
                              width: 13,
                              height: 13,
                              borderRadius: "50%",
                              bgcolor: entrenador.activo ? "success.main" : "grey.400",
                              border: "2px solid #fff",
                            }}
                          />
                        }
                      >
                        <Avatar
                          sx={{
                            width: 56,
                            height: 56,
                            fontSize: "1.15rem",
                            fontWeight: 700,
                            bgcolor: "rgba(34,51,107,0.10)",
                            color: "#22336B",
                          }}
                        >
                          {getInitials(entrenador.nombre)}
                        </Avatar>
                      </Badge>

                      <Box textAlign="center" sx={{ minWidth: 0, width: "100%" }}>
                        <Typography variant="h6" fontWeight={700} noWrap lineHeight={1.25}>
                          {entrenador.nombre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {getEquipoName(entrenador)}
                        </Typography>
                      </Box>

                      <Stack
                        direction="row"
                        spacing={0.75}
                        flexWrap="wrap"
                        useFlexGap
                        justifyContent="center"
                      >
                        <Chip
                          label={entrenador.rol === "coordinador" ? "Coordinador" : "Entrenador"}
                          color={entrenador.rol === "coordinador" ? "secondary" : "default"}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                        {entrenador.categoria_equipo_actual && (
                          <Chip
                            label={entrenador.categoria_equipo_actual}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Stack>

                      {entrenador.email && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ maxWidth: "100%" }}
                        >
                          {entrenador.email}
                        </Typography>
                      )}

                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEdit(entrenador)}
                            sx={{ color: "text.secondary", "&:hover": { color: "info.main" } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteTarget(entrenador)}
                            sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Fab
        color="primary"
        aria-label="Añadir entrenador"
        onClick={handleOpenCreate}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          color: "#fff",
          display: { xs: "flex", sm: "none" },
        }}
      >
        <AddIcon />
      </Fab>

      <ModalShell
        open={openModal}
        onClose={() => setOpenModal(false)}
        title={editMode ? "Editar entrenador" : "Nuevo entrenador"}
        subtitle={editMode ? currentEntrenador.nombre : "Credenciales y datos"}
        icon={GroupsIcon}
        actions={
          <>
            <Button onClick={() => setOpenModal(false)} sx={{ color: "text.secondary" }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<SaveIcon />}
              disabled={
                saving ||
                !currentEntrenador.nombre ||
                !currentEntrenador.email ||
                (!editMode && !currentEntrenador.password)
              }
              sx={{ borderRadius: 2 }}
            >
              {saving ? "Guardando..." : editMode ? "Guardar cambios" : "Crear entrenador"}
            </Button>
          </>
        }
      >
        <Stack spacing={2.5} mt={0.5}>
          <TextField
            label="Nombre completo"
            name="nombre"
            value={currentEntrenador.nombre}
            onChange={handleChange}
            fullWidth
            required
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={7}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={currentEntrenador.email}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                label="DNI"
                name="dni"
                value={currentEntrenador.dni}
                onChange={handleChange}
                fullWidth
                placeholder="12345678A"
              />
            </Grid>
          </Grid>
          {!editMode && (
            <TextField
              label="Contraseña"
              name="password"
              type={showPassword ? "text" : "password"}
              value={currentEntrenador.password}
              onChange={handleChange}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha de nacimiento"
                name="fecha_nacimiento"
                type="date"
                value={currentEntrenador.fecha_nacimiento}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Rol</InputLabel>
                <Select
                  name="rol"
                  value={currentEntrenador.rol}
                  label="Rol"
                  onChange={handleChange}
                  sx={{ height: 44 }}
                >
                  <MenuItem value="entrenador">Entrenador</MenuItem>
                  <MenuItem value="coordinador">Coordinador</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Autocomplete
            multiple
            options={equiposTemporada}
            getOptionLabel={(eq) => eq.nombre || ""}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            value={equiposTemporada.filter((eq) =>
              (currentEntrenador.equipos_actuales || []).includes(eq._id)
            )}
            onChange={(e, seleccion) =>
              setCurrentEntrenador((prev) => ({
                ...prev,
                equipos_actuales: seleccion.map((eq) => eq._id),
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Equipos asignados"
                placeholder={(currentEntrenador.equipos_actuales || []).length ? "" : "Sin equipo"}
              />
            )}
          />
          <FormControlLabel
            control={
              <Checkbox checked={currentEntrenador.activo} onChange={handleChange} name="activo" />
            }
            label="Entrenador activo"
          />
        </Stack>
      </ModalShell>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Eliminar entrenador"
        message={
          <>
            ¿Seguro que quieres eliminar a <b>{deleteTarget?.nombre}</b>? Perderá el acceso a la
            plataforma. Esta acción no se puede deshacer.
          </>
        }
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
        loading={saving}
      />

      <Footer />
    </DashboardLayout>
  );
}
